import classNames from 'clsx';
import type { MdComponentIndexProps } from '../type';
import { calcBgColor, calcBgLinearColor } from './calcBgColor';
import { calcImageBg } from './calcImageBg';
import { calcShadow } from './calcShadown';
import styles from './ImageBackground.module.css';

function transformUnitToNumber(val: any, defaultValue: number) {
  if (val == undefined) return defaultValue;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const number = parseFloat(val.endsWith('px') ? val.replace('px', '') : val);
    if (isNaN(number)) {
      return defaultValue;
    }
    return number;
  }
  return defaultValue;
}

function isString(val: any) {
  return typeof val === 'string' ? val : undefined;
}

function isEnum<T>(val: any, enumList: T[], defaultValue?: T): T {
  if (enumList.includes(val)) {
    return val;
  }
  return defaultValue ?? enumList[0];
}

export function MarkdownImageBackground(
  props: React.PropsWithChildren<
    {
      position?:
        | 'center'
        | 'top'
        | 'bottom'
        | 'left'
        | 'right'
        | 'topLeft'
        | 'topRight'
        | 'bottomLeft'
        | 'bottomRight';
      radius?: number;
      borderWidth?: number;
      shadow?: 'xsmall' | 'small' | 'middle' | 'large' | 'xlarge' | 'none';
      paddingX?: number;
      paddingY?: number;
      paddingTop?: number;
      paddingBottom?: number;
      paddingLeft?: number;
      paddingRight?: number;
      bgColor?: string;
      bgDirection?: number;
      bgGridOverlay?:
        | 'ploka'
        | 'ploka2'
        | 'paper'
        | 'paper2'
        | 'paper3'
        | 'wave'
        | 'electronics'
        | 'stars'
        | 'none';
      bgGridOverlayColor?: 'white' | 'gray' | 'black';
      bgRadius?: number;
    } & MdComponentIndexProps
  >,
) {
  const paddingX = transformUnitToNumber(props.paddingX, 56);
  const paddingY = transformUnitToNumber(props.paddingY, (paddingX * 5) / 7);
  const paddingLeft = transformUnitToNumber(props.paddingLeft, paddingX);
  const paddingRight = transformUnitToNumber(props.paddingRight, paddingX);
  const paddingTop = transformUnitToNumber(props.paddingTop, paddingY);
  const paddingBottom = transformUnitToNumber(props.paddingBottom, paddingY);
  // 背景圆角
  const bgRadius = transformUnitToNumber(props.bgRadius, 8);
  //
  const bgColor = isString(props.bgColor)?.trim() || 'preset1';
  // 背景渐变方向（度）? 觉得可以和 bgColor 融合而不需要另外一个颜色
  const bgDirection = transformUnitToNumber(props.bgDirection, 150);
  // 内置背景花纹
  const bgGridOverlay = isEnum(
    props.bgGridOverlay,
    [
      'ploka',
      'ploka2',
      'paper',
      'paper2',
      'paper3',
      'wave',
      'electronics',
      'stars',
      'none',
    ] as const,
    'none',
  );
  const bgGridOverlayColor = isEnum(
    props.bgGridOverlayColor,
    ['white', 'gray', 'black'] as const,
    'white',
  );
  const radius = transformUnitToNumber(props.radius, 4);
  const borderWidth = transformUnitToNumber(props.borderWidth, 0);
  const position = (isString(props.position) || 'center').toLowerCase();
  const top = position.includes('top');
  const bottom = position.includes('bottom');
  const left = position.includes('left');
  const right = position.includes('right');
  const shadow = isString(props.shadow) || 'middle';

  return (
    <div
      className={styles.ImageBackgroundWrapper}
      data-first={props['data-first']}
      data-last={props['data-last']}
    >
      <div
        className={styles.ImageBackgroundContainer}
        style={{
          paddingLeft,
          paddingRight,
          paddingTop,
          paddingBottom,
          borderRadius: bgRadius,
          // 背景色，渐变的背景色
          background: calcBgColor(bgColor) || calcBgLinearColor(bgColor, bgDirection),
        }}
      >
        <>
          <div
            className={styles.ImageBackgroundImage}
            style={{
              ...(bgGridOverlay === 'none' ? {} : calcImageBg(bgGridOverlay, bgGridOverlayColor)),
            }}
          />
        </>
        <div
          className={classNames(styles.ImageBackgroundContent, 'markdown-child-root')}
          style={{
            // borderRadius: radius,
            borderWidth,
            left: left ? `-${paddingLeft}px` : undefined,
            right: right ? `-${paddingRight}px` : undefined,
            top: top ? `-${paddingTop}px` : undefined,
            bottom: bottom ? `-${paddingBottom}px` : undefined,
            boxShadow: calcShadow(shadow),
            borderTopLeftRadius: top || left ? 0 : radius,
            borderTopRightRadius: top || right ? 0 : radius,
            borderBottomLeftRadius: bottom || left ? 0 : radius,
            borderBottomRightRadius: bottom || right ? 0 : radius,
          }}
        >
          {props.children}
        </div>
      </div>
    </div>
  );
}

MarkdownImageBackground.attributes = [
  'position',
  'radius',
  'borderWidth',
  'shadow',
  'paddingX',
  'paddingY',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'bgColor',
  'bgDirection',
  'bgGridOverlay',
  'bgGridOverlayColor',
  'bgRadius',
];
MarkdownImageBackground.inline = false;
